import { Figure, TextBackground, Reference, CustomLink } from "../components";
import { contributorLinks } from "../constants";

/**
 * Define the HTML code for the About page.
 * @returns HTML code for the About page
 */
export const About = () => {
	return (
		<section className="page">
			<TextBackground id="refs">
				<h2>References</h2>
				<div>
					If you used our web-based tool, the STEGG
					docker image or source code, please cite us.
					<ol>
						<li>
							<Reference
								title="pending"
								pdfLink="pending"
								publisherLink="pending"
							/>
						</li>
					</ol>
				</div>
			</TextBackground>
            <TextBackground>
				<h2>Algorithm</h2>
				<Figure
					imgName="method.png"
					caption="Algorithm."
					desc={
						"Overview of STEGG Algorithm"
					}
					width="100%"
				/>
				<div style={{ marginInline: "1rem" }}>
					<ol>
						<li>
							<b>Peptide-MHC Ensemble Generation:</b> The goal when modeling the 
							T-cell Receptor (TCR) is to accurately capture its conformational 
							diversity and flexibility, particularly in the crucial CDR loops that 
							interact with the pMHC complex. The modeling process begins by generating 
							an initial TCR structure using the tool tfold-TCR to 
							handle the conserved regions. The main focus then shifts to the 
							flexible CDR loops; our ensemble generation algorithm utilizes randomized 
							coordinate descent (RCD) to sample thousands of possible 
							backbone conformations for each of the six CDR loops. The most promising backbones are selected and 
							subsequently have their side chains added using the packing algorithm FASPR. 
							This procedure produces a set of diverse and relevant TCR models.
						</li>
						<li>
							<b>TCR Ensemble Generation:</b> The peptide-MHC (pMHC) complex is modeled using 
							APE-Gen 2.0 to generate an ensemble of 3D conformations that account for the 
							flexibility of the peptide within the binding groove. This tool employs both 
							homology modeling, referencing a library of known structures, and conformational 
							sampling. For sampling, the peptide section between anchor positions is treated 
							as a loop, and Randomized Coordinate Descent (RCD) is used to sample thousands of 
							backbone conformations. These sampled peptides are then docked back into the MHC 
							complex. A key modification for this work involved 
							replacing MODELLER with tfold-TCR's deep learning-based pMHC modeling framework, 
							which removes a dependency on a MODELLER license and allows modeling of MHCs lacking 
							PDB reference structures.
						</li>
						<li>
							<b>TCR-pMHC Ensemble Docking:</b> We use Haddock3 to perform the molecular 
							docking of the previously generated TCR and pMHC ensembles. We designate the TCR's 
							CDR3 loops and the peptide as the interacting residues. Our docking protocol consists of 
							rigid-body docking, flexible refinement, and energy minimization, 
							with clustering and filtering applied to the results after each step.
						</li>
						<li>
							<b>Filtering and Ranking:</b> The resulting TCR-pMHC complexes are refined through filtering 
							and ranking steps. Initial filtering is performed using geometric criteria (e.g.  
							checking distances between parts of the TCR and the pMHC and evaluating 
							the crossing/docking angles between the TCR and the pMHC). These criteria were derived from observed 
							structural patterns in the Protein Data Bank (PDB) and prior work. Since the rankings 
							provided directly by Haddock3 were found to only loosely correlate with structural quality metrics 
							like RMSD to crystal structures, a custom scoring approach was developed. For our scoring function, we calculate 
							the Rosetta energy terms for each complex and then apply a learned set of weights for final complex ranking.
						</li>
					</ol>
				</div>
			</TextBackground>
            <TextBackground>
				<h2>Dataset and Repository</h2>
				<div>
					To access the code for STEGG, please see the repo found here: {" "} 
					<CustomLink
						path="https://github.com/KavrakiLab/STEGGosaurus-docker"
						title="STEGG github"
						newTab
					/>
				</div>
				<div>
					To run STEGG locally, please use the docker container found here: <b>{" "}</b>
					<CustomLink
						path=""
						title="TODO"
						newTab
					/>
				</div>
			</TextBackground>
			<TextBackground>
				<h2>Funding</h2>
				<div>
					This work has been supported in part by the Informatics
					Technology for Cancer Research (ITCR) initiative of the
					National Cancer Institute (NCI) of NIH (1U01CA258512-01),
					and a training fellowship from the Gulf Coast Consortia 
					on the Training Program in Biomedical
					Informatics, National Library of Medicine 5T15LM007093.
				</div>
			</TextBackground>
			{/** Disclaimer section */}
			<TextBackground>
				<h2>Disclaimer</h2>
				<div>
					Due to the web server's nature, functionality is limited to
					specific inputs. If you would like to use the full
					functionality of STEGG, please visit the GitHub page
					and either install it locally or use the provided Docker
					image.
				</div>
			</TextBackground>
			{/** Privacy Policy section */}
			<TextBackground>
				<h2>Privacy Policy</h2>
				<div>
					Data and results of each user are accessible to other
					users. We assume any data submitted to STEGG is not confidential. 
					If you would like to run STEGG on sensative data, we recomend utilizing 
					a local instalation.
				</div>
			</TextBackground>
            {/** Contact Us Link */}
            <TextBackground>
					<h2>Contact us!</h2>
					<div>
                    <CustomLink
							path="/contactus"
							title="Contact Us"
							newTab={false}
						/>
                        <br />
						&nbsp;
						<br />
						We are open to ideas and suggestions, so don't hesitate
						to contact us!
						<br />
						&nbsp;
						<br />
						If you've had an issue with a particular STEGG job on
						the web server, please provide us with a link to that
						job. If you are reporting a bug, please send a detailed
						description of the error.
					</div>
				</TextBackground>
		</section>
	);
};
